import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Feature } from "geojson";

import PreferenceFieldButton, { trafficLightData, TrafficLightValue } from "fond/map/Field/PreferenceFieldButton";

import { getPathTypeCounts, getPreferenceCounts, getUniformPreference } from "./helper";

import { SectionGroup } from "./SelectedPaths.styles";

interface PreferenceSelectorProps {
  label: string;
  features: Feature[];
  value: string | null;
  onChange: (value: string | null) => void;
}

const PreferenceSelector: React.FC<PreferenceSelectorProps> = ({ label, features, value, onChange }) => {
  const counts = useMemo(() => getPreferenceCounts(features), [features]);
  const { crossings, paths } = getPathTypeCounts(features);

  const uniformPreference = useMemo(() => getUniformPreference(counts), [counts]);

  const activeValue = value ?? uniformPreference;

  const summary = [TrafficLightValue.prefer, TrafficLightValue.neutral, TrafficLightValue.avoid]
    .filter((pref) => counts[pref] > 0)
    .map((pref) => `${counts[pref]} ${trafficLightData[pref].text.toLowerCase()}`)
    .join(" · ");

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    onChange(newValue);
  };

  return (
    <SectionGroup>
      <div className="section-header">
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {paths > 0 ? `${paths} path${paths !== 1 ? "s" : ""}` : ""}
          {paths > 0 && crossings > 0 ? " · " : ""}
          {crossings > 0 ? `${crossings} crossing${crossings !== 1 ? "s" : ""}` : ""}
        </Typography>
      </div>
      <div className="section-body">
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          Currently {summary}
        </Typography>
        <ToggleButtonGroup
          value={activeValue}
          exclusive
          size="small"
          onChange={handleChange}
          sx={{ width: "100%", "& .MuiToggleButton-root": { flex: 1 } }}
        >
          {[TrafficLightValue.neutral, TrafficLightValue.avoid, TrafficLightValue.prefer].map((trafficLightValue) => (
            <PreferenceFieldButton key={trafficLightValue} trafficLightValue={trafficLightValue} disabled={false} value={activeValue ?? ""} />
          ))}
        </ToggleButtonGroup>
      </div>
    </SectionGroup>
  );
};

export default PreferenceSelector;
